import { useState } from 'react'
import { Database, Download, FileSpreadsheet, Link2, RotateCcw } from 'lucide-react'

import { todayIso } from '@cattle/money'
import type {
  Backup,
  BackupDownloadResponse,
  BackupListResponse,
  CreateBackupRequest,
  RestoreBackupRequest,
} from '@cattle/contracts'

import {
  BarList,
  Button,
  ConfirmDialog,
  Field,
  KeyValue,
  LoadingSkeleton,
  Modal,
  PageHeader,
  QueryBoundary,
  Section,
  StatusBadge,
  useToast,
  type BarListItem,
} from '../../../components'
import { useSession } from '../../../app/session'
import { queryKeys, useBackup } from '../../../fixtures'
import { get, post } from '../../../lib/apiClient'
import { cn } from '../../../lib/cn'
import { formatBytes, formatDate } from '../../../lib/format'
import { t, type MessageKey } from '../../../lib/i18n'
import { useApiMutation } from '../../../lib/mutations'

/**
 * O-05 · Backup & data.
 *
 * Two kinds of safety, kept deliberately apart: the nightly copies on this Mac
 * save you from a MISTAKE, and the download saves you from losing the MAC. The
 * book-only copy exists because 2 MB fits anywhere and photographs are
 * replaceable — the record is not.
 *
 * NOTHING ON THIS SCREEN IS OPTIMISTIC. "Saved" and "Healthy" are rendered only
 * when the payload says so; a backup screen that reports success while no backup
 * has ever run is the single worst screen in the product, because it is believed
 * right up until the day it matters.
 *
 * The same rule is why the download does not pretend. `GET /backups/:id/download`
 * returns a genuine signed link — and it points at `/backups/:id/file`, which no
 * route in `packages/contracts` serves. Rather than hand the reader a button that
 * silently does nothing, the link is shown with the reason it will not download.
 */

/** Older than this and the headline figure turns warn rather than ok. */
const BACKUP_WARN_DAYS = 7

/** The breakdown arrives labelled; these are the strings the reader sees. */
const BREAKDOWN_LABELS: Record<string, MessageKey> = {
  'Photos & video': 'o.backup.photosVideo',
  'Older copies': 'o.backup.olderCopies',
  'The book itself': 'o.backup.theBookItself',
}

const BREAKDOWN_TONES: BarListItem['tone'][] = ['accent', 'muted', 'warn']

/**
 * The newest copy that actually finished.
 *
 * `BackupState` is what this screen renders from and it carries no backup id —
 * it is a summary. Download and restore both need one, so the list is read at
 * the moment of the write rather than held in state: between the page load and
 * the button, the nightly job may have produced a newer copy, and that is the
 * one the reader means.
 */
async function newestReadyBackup(): Promise<Backup | null> {
  const list = await get<BackupListResponse>('/backups')
  // Ordered newest first by the server.
  return list.items.find((item) => item.status === 'READY') ?? null
}

export function BackupScreen() {
  const backup = useBackup()
  const toast = useToast()
  const { businessName } = useSession()
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [link, setLink] = useState<BackupDownloadResponse | null>(null)

  const make = useApiMutation<CreateBackupRequest['kind'], unknown>({
    run: async (kind) => post('/backups', { kind } satisfies CreateBackupRequest),
    invalidates: [queryKeys.backup, queryKeys.ownerOverview],
    success: t('o.backup.started'),
  })

  const download = useApiMutation<void, BackupDownloadResponse | null>({
    run: async () => {
      const target = await newestReadyBackup()
      if (!target) return null
      return get<BackupDownloadResponse>(`/backups/${target.id}/download`)
    },
    onDone: (result) => {
      if (result === null) {
        toast.show(t('o.backup.downloadNone'), 'danger')
        return
      }
      setLink(result)
    },
  })

  /*
    RESTORE.

    The server asks for the backup's own DATE typed back (`confirmDate`), which
    is a mechanical check against restoring the wrong copy — it is not the
    human confirmation. The human confirmation is the business name, typed into
    the dialog, exactly as the design has it; the date is read off the copy
    being restored and sent with it.

    The pre-restore snapshot is not a promise this screen invents: `restoreBackup`
    takes one before it touches a row, and the confirm says so.
  */
  const restore = useApiMutation<void, unknown>({
    run: async () => {
      const target = await newestReadyBackup()
      if (!target) return null
      // The same day the server will compute: the backup's date in the trader's
      // own timezone, not UTC — a copy taken after 7pm PKT is already tomorrow
      // in UTC and would fail the check.
      const body: RestoreBackupRequest = {
        confirmDate: todayIso('Asia/Karachi', new Date(target.startedAt)),
      }
      return post(`/backups/${target.id}/restore`, body)
    },
    // A restore replaces the book. Nothing cached survives it.
    invalidateAll: true,
    onDone: (result) =>
      toast.show(
        result === null ? t('o.backup.restoreNone') : t('o.backup.restoreStarted'),
        result === null ? 'danger' : 'info',
      ),
  })

  return (
    <>
      <PageHeader title={t('o.backup.title')} subtitle={t('o.backup.subtitle')} />

      <QueryBoundary
        query={backup}
        skeleton={
          <div className="grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
            <LoadingSkeleton variant="panel" />
            <LoadingSkeleton variant="panel" />
          </div>
        }
      >
        {(data) => {
          const daysAgo = data.lastFullDaysAgo
          const never = daysAgo === null
          const stale = never || daysAgo > BACKUP_WARN_DAYS

          const items: BarListItem[] = data.breakdown.map((entry, index) => {
            const key = BREAKDOWN_LABELS[entry.label]
            return {
              id: entry.label,
              label: key ? t(key) : entry.label,
              value: entry.mb,
              display: formatBytes(entry.mb),
              tone: BREAKDOWN_TONES[index] ?? 'accent',
            }
          })

          return (
            <>
              <div className="mb-3.5 grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
                <Section title={t('o.backup.lastFull')}>
                  <div
                    className={cn(
                      'font-display text-[34px] leading-none font-extrabold tracking-[-1px] tnum',
                      stale ? 'text-warn' : 'text-dark',
                    )}
                  >
                    {never ? t('o.backup.never') : t('o.backup.daysAgo', { days: daysAgo })}
                  </div>
                  <p className="mt-1.5 text-[13px] text-muted">
                    {data.lastFullDate
                      ? t('o.backup.savedTo', { date: formatDate(data.lastFullDate) })
                      : t('o.backup.neverBody')}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    <Button
                      variant="primary"
                      icon={<Download className="size-4" />}
                      disabled={make.isPending}
                      onClick={() => make.mutate('FULL')}
                    >
                      {t('o.backup.backupEverything')}
                    </Button>
                    <Button
                      icon={<Database className="size-4" />}
                      disabled={make.isPending}
                      onClick={() => make.mutate('BOOK_ONLY')}
                    >
                      {t('o.backup.bookOnly', { size: formatBytes(data.bookOnlyMb) })}
                    </Button>
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-muted">
                    {t('o.backup.sizeNote', { size: formatBytes(data.fullSizeMb) })}
                  </p>
                </Section>

                <Section title={t('o.backup.whatIsStored')}>
                  <BarList items={items} />
                  <p className="mt-3 border-t border-muted/15 pt-3 text-xs leading-relaxed text-muted">
                    {t('o.backup.bookNote')}
                  </p>
                </Section>
              </div>

              <div className="grid gap-3.5 md:grid-cols-2">
                <Section
                  title={t('o.backup.automatic')}
                  action={
                    data.automaticRunning ? (
                      <StatusBadge tone="ok">{t('o.backup.running')}</StatusBadge>
                    ) : (
                      <StatusBadge tone="danger">{t('label.off')}</StatusBadge>
                    )
                  }
                >
                  {/* Both of these were "Saved" and "Healthy" unconditionally.
                      A backup screen that says "Saved" when no backup has ever
                      run is the one screen that must never be optimistic. */}
                  <KeyValue
                    label={
                      /* The hour is `nightlyHourPkt` out of the same payload;
                         it used to be the literal string "02:00". */
                      data.nightlyHourPkt === null
                        ? t('o.backup.lastNightNoHour')
                        : t('o.backup.lastNight', {
                            time: `${String(data.nightlyHourPkt).padStart(2, '0')}:00`,
                          })
                    }
                    value={
                      <StatusBadge tone={data.lastNightlyStatus === 'READY' ? 'ok' : 'warn'}>
                        {data.lastNightlyStatus === 'READY'
                          ? t('o.backup.saved')
                          : (data.lastNightlyStatus ?? t('common.none'))}
                      </StatusBadge>
                    }
                  />
                  <KeyValue label={t('o.backup.copiesKept')} value={data.copiesKept} />
                  <KeyValue
                    label={t('o.backup.recordsCheck')}
                    value={
                      <StatusBadge tone={data.recordsCheck === 'READY' ? 'ok' : 'neutral'}>
                        {data.recordsCheck === 'READY'
                          ? t('o.backup.healthy')
                          : (data.recordsCheck ?? t('common.none'))}
                      </StatusBadge>
                    }
                  />
                  <p className="mt-3 border-t border-muted/15 pt-3 text-xs leading-relaxed text-muted">
                    {t('o.backup.automaticNote')}
                  </p>
                </Section>

                <Section title={t('o.backup.other')}>
                  <Field
                    layout="row"
                    label={t('o.backup.download')}
                    hint={t('o.backup.downloadHint')}
                  >
                    <Button
                      icon={<Link2 className="size-4" />}
                      disabled={download.isPending}
                      onClick={() => download.mutate()}
                    >
                      {t('o.backup.downloadAction')}
                    </Button>
                  </Field>
                  <Field
                    layout="row"
                    label={t('o.backup.exportExcel')}
                    hint={t('o.backup.exportExcelHint')}
                  >
                    <Button
                      icon={<FileSpreadsheet className="size-4" />}
                      onClick={() => toast.show(t('o.backup.exportExcel'), 'info')}
                    >
                      {t('act.export')}
                    </Button>
                  </Field>
                  <Field
                    layout="row"
                    label={t('o.backup.restore')}
                    hint={t('o.backup.restoreHint')}
                  >
                    <Button
                      variant="danger"
                      icon={<RotateCcw className="size-4" />}
                      disabled={restore.isPending}
                      onClick={() => setRestoreOpen(true)}
                    >
                      {t('o.backup.restoreAction')}
                    </Button>
                  </Field>
                </Section>
              </div>
            </>
          )
        }}
      </QueryBoundary>

      <ConfirmDialog
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onConfirm={() => restore.mutate()}
        tone="danger"
        disabled={restore.isPending}
        title={t('o.backup.restoreTitle', { name: businessName })}
        body={t('o.backup.restoreBody')}
        consequence={t('o.backup.restoreSnapshot')}
        confirmLabel={t('o.backup.restoreAction')}
        typeToConfirm={businessName}
      />

      {/*
        A real signed link, and the plain reason it will not download. The
        alternative — an <a download> pointed at a route that does not exist —
        would read to the trader as a broken backup rather than an unbuilt one.
      */}
      <Modal
        open={link !== null}
        onClose={() => setLink(null)}
        title={t('o.backup.downloadTitle')}
        footer={
          <Button variant="primary" onClick={() => setLink(null)}>
            {t('act.close')}
          </Button>
        }
      >
        <KeyValue
          label={t('o.backup.downloadSize')}
          value={formatBytes((link?.byteSize ?? 0) / (1024 * 1024))}
        />
        <KeyValue
          label={t('o.backup.downloadExpires')}
          value={link ? formatDate(link.expiresAt.slice(0, 10)) : t('common.none')}
        />
        <p className="mt-3 text-xs break-all text-muted select-all">{link?.url}</p>
        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('o.backup.downloadNotServed')}
        </p>
      </Modal>
    </>
  )
}
